import { useCallback, useEffect, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { mapAdminError } from '@/components/Admin/messages';
import { AdminError, type EscritorioAdmin } from '@/lib/plataforma_admin';
import { obterPessoa, type PessoaDetalhe } from '@/lib/plataforma_pessoa';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { DadosSecao, EscritorioSecao, PapelSecao, type Executar } from './PessoaSecoes';
import { AcessoSecao, SenhaSecao } from './PessoaSecoesAcesso';

interface PessoaDialogProps {
  usuarioId: string;
  escritorios: EscritorioAdmin[];
  onFechar(): void;
  /** A lista recarrega depois de toda escrita; a exclusão também fecha o painel. */
  onAlterada(): void;
}

function _mensagem(err: unknown): string {
  return err instanceof AdminError ? mapAdminError(err.reason) : ptBR.admin.erroGenerico;
}

/**
 * O painel da pessoa no console (E175.F1, ADR-0127). // ux-neutrality:allow doc-comment
 *
 * Cada seção salva sozinha e relê a pessoa do servidor depois: o que a tela // ux-neutrality:allow doc-comment
 * mostra é sempre o estado gravado, e a recusa aparece com a frase do motivo. // ux-neutrality:allow doc-comment
 */
export function PessoaDialog({ usuarioId, escritorios, onFechar, onAlterada }: PessoaDialogProps) {
  const apiClient = useApiClient();
  const [pessoa, setPessoa] = useState<PessoaDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setPessoa(await obterPessoa(apiClient, usuarioId));
    } catch (err) {
      setErro(_mensagem(err));
    }
  }, [apiClient, usuarioId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const executar: Executar = async (acao, mensagem, opcoes) => {
    setSalvando(true);
    setErro(null);
    setSucesso(null);
    try {
      await acao();
      onAlterada();
      if (opcoes?.fechar) {
        onFechar();
        return true;
      }
      setSucesso(mensagem);
      await carregar();
      return true;
    } catch (err) {
      setErro(_mensagem(err));
      return false;
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && !salvando && onFechar()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="pessoa-dialog">
        <DialogHeader>
          <DialogTitle>
            {pessoa?.display_name ?? pessoa?.email ?? ptBR.admin.pessoaCarregando}
          </DialogTitle>
          <DialogDescription>{pessoa?.display_name ? pessoa.email : ''}</DialogDescription>
        </DialogHeader>
        {sucesso !== null ? (
          <p role="status" data-testid="pessoa-sucesso" className="text-body-sm text-accent-700">
            {sucesso}
          </p>
        ) : null}
        {erro !== null ? (
          <p role="alert" data-testid="pessoa-erro" className="text-body-sm text-danger-700">
            {erro}
          </p>
        ) : null}
        {pessoa === null ? null : (
          <div className="flex flex-col gap-sc-3">
            <DadosSecao pessoa={pessoa} salvando={salvando} executar={executar} />
            <PapelSecao pessoa={pessoa} salvando={salvando} executar={executar} />
            {pessoa.ambiente === 'banco' ? null : (
              <EscritorioSecao
                pessoa={pessoa}
                escritorios={escritorios}
                salvando={salvando}
                executar={executar}
              />
            )}
            <SenhaSecao pessoa={pessoa} salvando={salvando} executar={executar} />
            <AcessoSecao pessoa={pessoa} salvando={salvando} executar={executar} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
